import React, { useState, useEffect } from 'react';
import API_URL from '../apiUrl';
import './admin.css';
import { fetchVideos } from './api';
import VideoPlayer from './VideoPlayer';

const MediaManager = () => {
  const [files, setFiles] = useState([]);
  const [fileInput, setFileInput] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [videos, setVideos] = useState([]);

  const fetchMedia = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/media/`);
      if (!res.ok) throw new Error('Failed to load media');
      const data = await res.json();
      setFiles(data);
    } catch (err) {
      setError(err.message || 'Error fetching media');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
    fetchVideos().then(setVideos).catch(() => {});
  }, []);

  const handleFileChange = (e) => {
    setFileInput(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!fileInput) {
      setError('Please choose a file to upload');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const form = new FormData();
      form.append('file', fileInput);
      if (nameInput) form.append('name', nameInput);

      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/media/`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: form,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Upload failed');
      }

      setSuccess('Uploaded successfully');
      setNameInput('');
      setFileInput(null);
      // reset file input value
      document.getElementById('media-file-input').value = '';
      fetchMedia();
    } catch (err) {
      setError(err.message || 'Upload error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (url) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // fallback
        const el = document.createElement('textarea');
        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setSuccess('URL copied to clipboard');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to copy URL');
    }
  };

  return (
    <div className="admin-media">
      <h1>Media</h1>

      <div className="media-upload">
        <form onSubmit={handleUpload}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input id="media-file-input" type="file" onChange={handleFileChange} />
            <input
              type="text"
              placeholder="Optional name"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
            />
            <button className="btn-primary-primary" type="submit" disabled={loading}>
              {loading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
        {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        {success && <div style={{ color: 'green', marginTop: 8 }}>{success}</div>}
      </div>

      <div style={{ marginTop: 20 }}>
        {loading && files.length === 0 ? (
          <div>Loading media...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8 }}>Name</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Type</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Preview</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.length === 0 ? (
                <tr><td colSpan="4">No media uploaded yet.</td></tr>
              ) : (
                files.map(item => (
                  <tr key={item.id}>
                    <td style={{ padding: 8 }}>{item.name || (item.file && item.file.split('/').pop())}</td>
                    <td style={{ padding: 8 }}>{item.file_type || '-'}</td>
                    <td style={{ padding: 8 }}>
                      {item.file ? (
                        <a href={item.file_url || item.file} target="_blank" rel="noopener noreferrer">Open</a>
                      ) : ('-')}
                    </td>
                    <td style={{ padding: 8 }}>
                      {item.file_url ? (
                        <button className="btn-secondary" onClick={() => handleCopy(item.file_url)}>Copy URL</button>
                      ) : (
                        <button className="btn-secondary" onClick={() => handleCopy(item.file)}>Copy URL</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 40 }}>
        <h2>Videos</h2>
        {videos.length === 0 ? (
          <div>No videos uploaded yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            {videos.map(video => (
              <VideoPlayer 
                key={video.id} 
                video={video}
                showTitle={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaManager;
